import React from "react";
import { Icon, MessageBar, MessageBarType } from "@fluentui/react";
import styled from "styled-components";
import { Toggle } from "@fluentui/react/lib/Toggle";
import { InitialStateType, Partition } from "../types";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";
import { useIntl, FormattedMessage } from "react-intl";
import { wizardMessages } from "../messages";
import { useTheme } from "cayo.ui";

type SelectDomainsAndPartitionsProps = {
  state: InitialStateType;
  dispatch: (data: any) => void;
};

const SelectDomainsAndPartitions = ({ state, dispatch }: SelectDomainsAndPartitionsProps) => {
  const intl = useIntl();
  const { cayoTheme } = useTheme();
  const accountName = state?.credentials?.user;
  return (
    <Wrapper>
      <MessageBar
        messageBarType={MessageBarType.info}
        truncated={true}
        styles={{
          root: {
            marginBottom: 20,
            background: cayoTheme.wizard.colors.active,
          },
        }}
      >
        {intl.formatMessage(wizardMessages.selectDomainsAndPartitionsInfo)}
      </MessageBar>
      {state.partitions?.length > 0 && (
        <AddedPartitions>
          {intl.formatMessage(wizardMessages.selectDomainsAndPartitionsAddedTitle)}
          <TooltipHost
            content={intl.formatMessage(wizardMessages.selectDomainsAndPartitionsAddedDesc)}
            closeDelay={500}
            id={"tooltipId"}
            styles={{ root: { fontSize: "14px" } }}
          >
            <Icon iconName={"Info"} aria-describedby={"tooltipId"} />
          </TooltipHost>
        </AddedPartitions>
      )}
      {state.partitions?.map((p: Partition) => {
        const checked = state.selectedPartitions.find(
          (i: Partition) => i.contextId === p.contextId
        );
        return (
          <Record key={p.contextId}>
            <div className="control">
              {p.managed && <StyledIcon iconName={"CompletedSolid"} />}
              {!p.managed && (
                <Toggle
                  checked={!!checked}
                  onChange={(_, checked) => {
                    dispatch({
                      type: "update-selected-partitions",
                      payload: {
                        checked,
                        partition: p,
                      },
                    });
                  }}
                />
              )}
            </div>
            {p.displayName}
          </Record>
        );
      })}

      {state.partitionsNoPermission.length > 0 && (
        <PartitionsNoPermission>
          <MessageBar
            messageBarType={MessageBarType.warning}
            truncated={true}
            styles={{
              icon: { color: "black" },
              root: {
                color: "black",
              },
            }}
          >
            {intl.formatMessage(wizardMessages.selectDomainsAndPartitionsSkipTitle, {
              accountName: `"${accountName}"`,
              LearnMore: (
                <a
                  href={"https://support.cayosoft.com/hc/en-us/articles/360041734032"}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage
                    id={wizardMessages.LearnMore.id}
                    defaultMessage={wizardMessages.LearnMore.defaultMessage}
                  />
                </a>
              ),
            })}
          </MessageBar>

          {state.partitionsNoPermission?.map((p: Partition) => (
            <PartitionName key={p.contextId}>{p.displayName}</PartitionName>
          ))}
        </PartitionsNoPermission>
      )}
    </Wrapper>
  );
};

export const StyledIcon = styled(Icon)`
  color: ${(props) => props.theme.cayoTheme.brandColors.success};
  padding-right: 10px;
`;

const Record = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-weight: 500;
  padding-left: 10px;
`;

const AddedPartitions = styled.p`
  display: flex;
  gap: 5px;
  align-items: baseline;
  font-weight: 500;
  font-size: 18px;
  margin: 0;
`;

const PartitionName = styled.p`
  font-weight: 500;
`;

const PartitionsNoPermission = styled.div``;

const Wrapper = styled.div`
  max-height: 64vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export default SelectDomainsAndPartitions;
